import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import ThemeConstants from '../../../../config/theme/theme_constants';
import { useEffect, useMemo } from 'react';
import ArrowBackIcon from '../../../../assets/images/arrow_back_black_icon.svg?react';
import { UserInterface } from '../../../../core/components/user/domain/interfaces/user_interface';
import { useGetResourceByThemeId } from '../../domain/hooks/use_resources_by_theme_id';
import { useGetFavoritesByUserIdAndThemeId } from '../../domain/hooks/use_get_favorites_by_user_id_and_theme_id';
import ResourceGridItem from '../components/resource/resource_grid_item';
import { SyncLoader } from 'react-spinners';
import { useGetThemeById } from '../../domain/hooks/use_theme';
import { CareerStatusEnum } from '../../../../core/enum/career_status_enum';
import { getCurrentUserTheme } from '../../domain/utils/get_current_user_theme';
import type { TFunction } from 'i18next';
import { useTranslation } from '../../../../i18n/useTranslation';

const theme = ThemeConstants();

const colorByType: Record<string, string> = {
  favorite: theme.UvibesBlue,
  audio: theme.UvibesOrange,
  article: theme.UvibesGreen,
  video: theme.UvibesPink,
};

const formatTypeName = (type: string, t: TFunction) => {
  switch (type) {
    case 'favorite':
      return t('course.resource.type_favorite');
    case 'audio':
      return t('course.resource.type_audio');
    case 'article':
      return t('course.resource.type_article');
    case 'video':
      return t('course.resource.type_video');
    default:
      return type;
  }
}

const ResourceTypePage: React.FC = () => {
  const { t } = useTranslation();
  const { type, themeId } = useParams();
  const navigate = useNavigate();
  const { course_theme, userId, company, careerStatus } = useOutletContext<UserInterface>();
  const currentTheme = useMemo(() => getCurrentUserTheme(course_theme, company?.id), [course_theme, company?.id]);
  const { data: resourcesData, isLoading: isGettingResources, error: resourceError, refetch: refetchResources } = useGetResourceByThemeId(themeId, false);
  const { favorites, isLoading: isGettingFavorites, error: favoriteError, refetch: refetchFavorites } = useGetFavoritesByUserIdAndThemeId(userId, themeId, false);
  const { error: themeError} = useGetThemeById(themeId);

  useEffect(() => {
    if (company?.permissions.course === true && careerStatus === CareerStatusEnum.Start) {
      navigate('/career/start', { replace: true });
    }
  }, [careerStatus, company?.permissions.course, navigate]);

  useEffect(() => {
    if (themeError) {
      const data = themeError.response?.data as { message?: string } | undefined;
      const errorMessage = data?.message || 'An error occurred';
      if (errorMessage === "id contains invalid characters" || errorMessage === "Theme not found") {
        toast.error(t('course.theme_not_found'));
        navigate('/career/start', { replace: true });
      }
    }
  }, [themeError, navigate, t]);

  useEffect(() => {
   if (!type || !colorByType[type]) {
     navigate('/career/start', { replace: true });
   }
  }, [type, navigate]);

  useEffect(() => {
    const currentThemeId = currentTheme ? typeof currentTheme._themeId === 'string' ? currentTheme._themeId : currentTheme._themeId?._id : undefined;
    if (!currentThemeId && company?.permissions.ressource === false) {
      navigate('/career/start', { replace: true });
    } else if (currentThemeId && currentThemeId !== themeId) {
      navigate(`/career/${currentThemeId}/resources`, { replace: true });
    }
  }, [navigate, currentTheme, company?.permissions.ressource, themeId]);

  const color = type ? colorByType[type] : theme.UvibesPink;

  const elementsToDisplay = useMemo(() => {
    if (type === 'favorite') {
      return favorites ?? [];
    }
    return resourcesData?.filter(resource => resource.type === type) ?? [];
  }, [type, resourcesData, favorites]);

  return (
    <div className="min-h-dvh py-[5.6dvh] max-lg:px-[3%] lg:px-[1%] flex flex-col gap-9 items-center">
      <div className='flex gap-12.5 items-center w-full md:justify-between'>
        <button className='cursor-pointer self-start w-6 h-6 flex items-center justify-center' onClick={() => navigate(-1)}>
          <ArrowBackIcon color='black' className='w-4.25 h-auto'/>
        </button>

        <h1 className='text-xl font-bold' style={{ color: color }}>
          {formatTypeName(type || t('course.resource.unknown'), t)}
        </h1>
        
        <div/>
      </div>

        {(type === 'favorite' ? isGettingFavorites : isGettingResources) && (
          <div className="absolute top-0 left-0 w-full h-full flex justify-center flex-col items-center">
            <SyncLoader color={color} size={24} speedMultiplier={0.5} />
          </div>
        )}
        {(type === 'favorite' ? favoriteError : resourceError) && (
          <div className="absolute top-0 left-0 w-full h-full flex justify-center flex-col items-center gap-3">
            <span className="text-sm text-medium text-center">{t('common.error.occurred')}</span>
            <button
                className="px-4 py-2 rounded-[18px] text-white font-semibold text-sm cursor-pointer"
                style={{ background: theme.UvibesPink }}
                onClick={() => (type === 'favorite' ? refetchFavorites() : refetchResources())}
            >
                {t('common.button.retry')}
            </button>
          </div>
        )}
        
      {(elementsToDisplay.length === 0 && (type === 'favorite' ? !isGettingFavorites : !isGettingResources)) ? (
        <p className='text-center text-sm'>{t('course.resource.no_results')}</p>
      ) : (
        <div className="grid max-sm:grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-5 gap-y-4">
          {elementsToDisplay.map((resource) => (
            <ResourceGridItem key={resource._id} id={resource._id} title={resource.title} color={color} pictureUrl={resource.picture} />
          ))}
        </div>
      )}
    </div>
  );
};

export default ResourceTypePage;
